import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import osutoriLogo from "@/assets/logos/osutori-white.png";
import onvajouerLogo from "@/assets/logos/onvajouer-white.png";
import hybforceLogo from "@/assets/logos/hybforce-white.png";
import shayLogo from "@/assets/logos/shay-white.png";
import goapLogo from "@/assets/logos/goap-white.png";
import forevadifferentLogo from "@/assets/logos/forevadifferent-white.png";
import wensjegelukLogo from "@/assets/logos/wensjegeluk-white.png";
import laLuunLogo from "@/assets/logos/la-luun-baby-logo-white.png";
import diamondLogo from "@/assets/logos/diamond-detailing-white.png";
import candiezLogo from "@/assets/logos/candiez-white.png";

const logos = [
  { src: hybforceLogo, alt: "Hybforce Eyewear" },
  { src: onvajouerLogo, alt: "Onvajouer" },
  { src: forevadifferentLogo, alt: "Forevadifferent" },
  { src: shayLogo, alt: "Shay" },
  { src: goapLogo, alt: "GOAP" },
  { src: osutoriLogo, alt: "Osutori" },
  { src: diamondLogo, alt: "Diamond Detailing" },
  { src: candiezLogo, alt: "Candiez BV" },
  { src: wensjegelukLogo, alt: "Wensjegeluk" },
  { src: laLuunLogo, alt: "La Luun Baby" },
];

import imgOsutoriAsset from "@/assets/portfolio/osutori_1.jpg.asset.json";
const imgOsutori = imgOsutoriAsset.url;
import imgDiamondAsset from "@/assets/portfolio/diamonddetailing_2.jpg.asset.json";
const imgDiamond = imgDiamondAsset.url;
import imgForevaAsset from "@/assets/portfolio/forevadifferent_6.jpg.asset.json";
const imgForeva = imgForevaAsset.url;
import imgGoapAsset from "@/assets/portfolio/goap-product.jpg.asset.json";
const imgGoap = imgGoapAsset.url;
import imgLaLuunAsset from "@/assets/portfolio/laluunbaby.jpg.asset.json";
const imgLaLuun = imgLaLuunAsset.url;
import imgCandiezAsset from "@/assets/portfolio/candiez-candy.png.asset.json";
import imgRvnche from "@/assets/portfolio/rvnche-square.webp";
import imgQardemon from "@/assets/portfolio/qardemon-watches.webp";
import imgHybforceAsset from "@/assets/portfolio/hybforce-founder.png.asset.json";
const imgHybforce = imgHybforceAsset.url;

const imgCandiez = imgCandiezAsset.url;

type Review = { image: string; quote: string; name: string; role: string; objectPosition?: string; growth?: string };

type Variant = "home" | "webshop" | "email";

const hybforceQuotes: Record<Variant, string> = {
  home: "Nextconvert heeft onze Shopify webshop opnieuw opgebouwd én onze Klaviyo flows ingericht. Sinds de nieuwe build en flows zien we een duidelijke stijging in zowel conversie als omzet uit e-mail.",
  webshop: "Nextconvert heeft onze Shopify webshop volledig opnieuw opgebouwd. Strakke uitstraling, snellere productpagina's en een checkout die merkbaar beter converteert. Fijne samenwerking van A tot Z.",
  email: "Onze e-mailmarketing hebben we volledig uit handen gegeven aan Nextconvert. Ze runnen alle Klaviyo flows en campagnes en genereren zo'n 30% extra omzet, puur vanuit e-mail.",
};


const buildReviews = (variant: Variant): Review[] => {
  const reviews: Review[] = [
    {
      image: imgHybforce,
      quote: hybforceQuotes[variant],
      name: "Niels",
      role: "Founder Hybforce",
    },
    {
      image: imgOsutori,
      quote:
        "Nextconvert heeft onze Shopify webshop compleet opnieuw opgebouwd én onze Klaviyo flows ingericht. De conversie én omzet uit e-mail zijn beide flink omhoog gegaan.",
      name: "Romy",
      role: "Founder Osutori",
    },
    {
      image: imgDiamond,
      quote:
        "Van een basic webshop naar een echte verkoopmachine. Nieuwe Shopify build, sterke productpagina's en Klaviyo flows die dag en nacht doorlopen. Top samenwerking.",
      name: "Sven",
      role: "Co-Founder Diamond Detailing",
    },
    {
      image: imgForeva,
      quote:
        "Onze e-mailmarketing had weinig structuur. Nextconvert heeft alle Klaviyo flows opnieuw opgezet en pakt structureel campagnes op. Alles staat nu strak geautomatiseerd.",
      name: "John",
      role: "Founder Forevadifferent",
    },
    {
      image: imgGoap,
      quote:
        "Fijne samenwerking van begin tot eind. De Shopify webshop ziet er strak uit, laadt snel en converteert echt beter. Wordt goed meegedacht en snel geschakeld.",
      name: "Jasmijn",
      role: "Founder GOAP",
    },
    {
      image: imgLaLuun,
      quote:
        "De nieuwe Shopify webshop straalt precies uit wat ik voor ogen had. Klanten haken minder af in de checkout en de conversie is echt merkbaar omhoog gegaan.",
      name: "Lara",
      role: "Founder La luun baby",
    },
    {
      image: imgCandiez,
      quote:
        "Nextconvert heeft onze SEO compleet opgepakt. We krijgen nu 33.000 nieuwe gebruikers per maand binnen via organisch verkeer. Enorme groei zonder extra advertentiekosten.",
      name: "Sylvia",
      role: "Co-Founder Candiez B.V",
      growth: "+84% groei",
    },
    {
      image: imgRvnche,
      quote:
        "Nextconvert heeft onze Shopify webshop en Klaviyo flows helemaal neergezet. Strakke uitstraling, hogere conversie en een e-mailkanaal dat elke maand blijft doorgroeien.",
      name: "Founder",
      role: "Rvnche",
    },
    {
      image: imgQardemon,
      quote:
        "Duidelijke communicatie, snelle uitvoering en resultaten die er zijn. Sinds de nieuwe Shopify build en Klaviyo flows zijn onze herhaalaankopen merkbaar omhoog.",
      name: "Matz",
      role: "Founder Qardemon Watches",
    },
  ];

  return reviews;
};

const EmailReviewsSection = ({ variant = "email" }: { variant?: Variant } = {}) => {
  const reviews = buildReviews(variant);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    containScroll: false,
    dragFree: false,
    duration: 22,
    watchDrag: true,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section className="bg-primary w-full py-16 md:py-24 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {/* Logos - staggered, wit */}
        {/* Mobiel: meerdere rijen met wisselend aantal (3-2-3-2) */}
        <div className="md:hidden mb-12 space-y-6">
          {[
            logos.slice(0, 3),
            logos.slice(3, 5),
            logos.slice(5, 8),
            logos.slice(8, 10),
          ].map((row, rowIdx) => (
            <div key={rowIdx} className="flex flex-nowrap items-center justify-center gap-x-6">
              {row.map((l, i) => (
                <div key={i} className="flex items-center justify-center h-8 max-w-[28%]">
                  <img src={l.src} alt={l.alt} className="max-h-full max-w-full object-contain opacity-95" loading="lazy" />
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Desktop: 2 rijen van 5 */}
        <div className="hidden md:block mb-[74px] space-y-10">
          {[
            { items: logos.slice(0, 5), sizes: ["h-12", "h-14", "h-12", "h-14", "h-12"] },
            { items: logos.slice(5, 10), sizes: ["h-14", "h-12", "h-14", "h-12", "h-14"] },
          ].map((row, rowIdx) => (
            <div key={rowIdx} className="flex flex-nowrap items-center justify-between gap-x-10">
              {row.items.map((l, i) => (
                <div key={i} className={`flex items-center justify-center min-w-0 flex-1 ${row.sizes[i]}`}>
                  <img src={l.src} alt={l.alt} className="max-h-full max-w-full object-contain opacity-95" loading="lazy" />
                </div>
              ))}
            </div>
          ))}
        </div>

      </div>

      {/* Swipable reviews - full bleed */}
      <div className="relative w-full">
        <div className="overflow-hidden select-none [touch-action:pan-y]" ref={emblaRef}>
          <div className="flex [touch-action:pan-y]">

            {reviews.map((r, i) => (
              <div key={i} className="flex-[0_0_86%] md:flex-[0_0_61%] min-w-0 px-3 md:px-[35px] flex">
                <div
                  className={`rounded-[32px] overflow-hidden flex flex-col md:grid md:grid-rows-1 md:grid-cols-2 bg-cream md:aspect-[2/1] w-full transition-opacity duration-300 ${
                    selectedIndex === i ? "opacity-100" : "opacity-60"
                  }`}
                >
                  <div className="overflow-hidden aspect-square md:aspect-auto md:h-full">
                    <img src={r.image} alt={r.name} draggable={false} className="w-full h-full object-cover pointer-events-none select-none" style={{ objectPosition: r.objectPosition ?? "center top" }} loading="lazy" />
                  </div>
                  <div className="p-6 md:p-9 flex flex-col justify-center flex-1 select-none relative">
                    {r.growth && (
                      <span className="absolute top-4 right-4 md:top-6 md:right-6 text-neutral-400 text-xs md:text-sm font-medium">
                        {r.growth}
                      </span>
                    )}
                    <div className="text-accent text-6xl md:text-8xl leading-none -mb-1 md:mb-2" aria-hidden>&#8220;</div>
                    <p className="text-neutral-900 text-sm md:text-base leading-[1.55] md:leading-[1.8] mb-4 md:mb-8 max-w-[420px] md:max-w-[360px]">
                      {r.quote}
                    </p>
                    <div>
                      <p className="text-neutral-900 font-bold text-sm md:text-base">{r.name}</p>
                      <p className="text-neutral-600 text-xs md:text-sm mt-1">{r.role}</p>

                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => emblaApi?.scrollPrev()}
          aria-label="Vorige review"
          className="absolute left-[7%] md:left-[19.5%] top-1/2 -translate-y-1/2 -translate-x-1/2 md:translate-x-0 w-5 h-5 md:w-10 md:h-10 min-w-0 min-h-0 p-0 flex items-center justify-center text-white hover:text-white/80 transition z-20"
        >
          <ChevronLeft className="w-4 h-4 md:w-10 md:h-10" strokeWidth={1.5} />
        </button>
        <button
          onClick={() => emblaApi?.scrollNext()}
          aria-label="Volgende review"
          className="absolute right-[7%] md:right-[19.5%] top-1/2 -translate-y-1/2 translate-x-1/2 md:translate-x-0 w-5 h-5 md:w-10 md:h-10 min-w-0 min-h-0 p-0 flex items-center justify-center text-white hover:text-white/80 transition z-20"
        >
          <ChevronRight className="w-4 h-4 md:w-10 md:h-10" strokeWidth={1.5} />
        </button>
      </div>


    </section>
  );
};

export default EmailReviewsSection;
